/**
 * Bulk Actions Bar
 * Enhanced multi-select operations for lists
 * Part of Phase 6E - UX Enhancements
 */

import React from 'react';
import {
  Space,
  Button,
  Dropdown,
  Modal,
  Progress,
  Typography,
  message,
  Badge,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

const { Text } = Typography;

export interface BulkAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  danger?: boolean;
  confirm?: boolean;
  confirmTitle?: string;
  disabled?: boolean;
  /** The action reports its own outcome (e.g. partial failures); skip the generic toast. */
  selfReporting?: boolean;
  onClick: (selectedIds: string[]) => Promise<void> | void;
}

interface BulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll?: () => void;
  onClearSelection: () => void;
  actions?: BulkAction[];
  selectedIds: string[];
  visible?: boolean;
  /** Placeholder "Export / Delete Selected" actions; turn off when the page supplies real ones. */
  includeDefaultActions?: boolean;
}

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  actions = [],
  selectedIds,
  visible = true,
  includeDefaultActions = true,
}) => {
  const [processing, setProcessing] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [currentAction, setCurrentAction] = React.useState<string>('');

  if (!visible || selectedCount === 0) {
    return null;
  }

  const handleAction = async (action: BulkAction) => {
    if (action.confirm) {
      Modal.confirm({
        title: action.confirmTitle || `${action.label}?`,
        content: `Are you sure you want to ${action.label.toLowerCase()} ${selectedCount} item(s)?`,
        okText: 'Yes',
        cancelText: 'No',
        okButtonProps: { danger: action.danger },
        onOk: async () => {
          await executeAction(action);
        },
      });
    } else {
      await executeAction(action);
    }
  };

  const executeAction = async (action: BulkAction) => {
    try {
      setProcessing(true);
      setCurrentAction(action.label);
      setProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      await action.onClick(selectedIds);

      clearInterval(progressInterval);
      setProgress(100);

      if (!action.selfReporting) message.success(`${action.label} completed for ${selectedCount} item(s)`);
      onClearSelection();
    } catch (error) {
      console.error(`Bulk action failed:`, error);
      message.error(`Failed to ${action.label.toLowerCase()}`);
    } finally {
      setTimeout(() => {
        setProcessing(false);
        setProgress(0);
        setCurrentAction('');
      }, 500);
    }
  };

  // Default actions
  const defaultActions: BulkAction[] = [
    {
      key: 'export',
      label: 'Export Selected',
      icon: <ExportOutlined />,
      onClick: async (ids) => {
        // Export logic here
        console.log('Exporting:', ids);
      },
    },
    {
      key: 'delete',
      label: 'Delete Selected',
      icon: <DeleteOutlined />,
      danger: true,
      confirm: true,
      confirmTitle: 'Delete items?',
      onClick: async (ids) => {
        // Delete logic here
        console.log('Deleting:', ids);
      },
    },
  ];

  const allActions = includeDefaultActions ? [...actions, ...defaultActions] : actions;

  const menuItems: MenuProps['items'] = allActions.map((action) => ({
    key: action.key,
    icon: action.icon,
    label: action.label,
    danger: action.danger,
    disabled: action.disabled,
    onClick: () => handleAction(action),
  }));

  return (
    <>
      <div
        className="bulk-actions-bar-floating"
        style={{
          position: 'fixed',
          bottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: 8,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          maxWidth: 'min(720px, calc(100vw - 24px))',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Badge count={selectedCount} showZero>
          <CheckCircleOutlined style={{ fontSize: 20, color: '#145A54' }} />
        </Badge>

        <Text strong>
          {selectedCount} of {totalCount} selected
        </Text>

        <div style={{ borderLeft: '1px solid #d9d9d9', height: 24 }} />

        <Space wrap size={[8, 8]}>
          {onSelectAll && selectedCount < totalCount && (
            <Button type="link" onClick={onSelectAll}>
              Select All ({totalCount})
            </Button>
          )}

          {allActions
            .filter((a) => !a.danger)
            .slice(0, 2)
            .map((action) => (
              <Button
                key={action.key}
                icon={action.icon}
                onClick={() => handleAction(action)}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}

          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button icon={<MoreOutlined />}>More Actions</Button>
          </Dropdown>

          <Button
            icon={<CloseCircleOutlined />}
            onClick={onClearSelection}
            type="text"
          >
            Clear
          </Button>
        </Space>
      </div>

      {/* Processing Modal */}
      <Modal
        open={processing}
        title={currentAction}
        footer={null}
        closable={false}
        centered
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Processing {selectedCount} item(s)...</Text>
          <Progress percent={progress} status="active" />
        </Space>
      </Modal>
    </>
  );
};

export default BulkActionsBar;
